/**
 * Mock data for ExecutionCockpitDailyBriefV6
 *
 * Content ported from: docs/ui-concepts/homepage-cockpit/helix-execution-cockpit-v6.jsx
 */

import type { ExecutionCockpitDailyBriefDto } from '@helix/lib';

export const dailyBriefV6Mock: ExecutionCockpitDailyBriefDto = {
  asOf: '2025-01-17T08:42:00-08:00',

  filters: {
    strategyLabel: 'FY25 Strategic Plan',
    timeWindowLabel: 'Q1 2025',
    lensLabel: 'Company-wide',
    pinnedLenses: [
      { id: 'global', label: 'Global' },
      { id: 'americas', label: 'Americas' },
      { id: 'emea', label: 'EMEA' },
      { id: 'apac', label: 'APAC' },
    ],
    activePinnedLensId: 'global',
  },

  executiveVerdict: {
    whatWillMiss:
      'Cloud Migration Phase 2 will slip by ~6 weeks without intervention.',
    bindingConstraint:
      'Engineering capacity deficit. Senior SRE roles unfilled; recruiting pipeline below plan.',
    decisionThatChangesOutcome:
      'Reallocate 2 FTEs from Analytics (delays delivery by ~2w) or approve a short-term contractor burst.',
    statusCounts: { green: 2, amber: 2, red: 1 },
  },

  riskRadar: [
    {
      id: 'strategic_bets_at_risk',
      label: 'Strategic Bets at Risk',
      count: 2,
      delta: 1,
      severity: 'critical',
      topDriver: 'Cloud Migration capacity gap; AI/ML infra constraint',
    },
    {
      id: 'role_decisions_pending',
      label: 'Role Decisions Pending',
      count: 4,
      delta: 0,
      severity: 'warning',
      topDriver: 'Create: 2 SRE, 1 PM • Move: Analytics lead',
    },
    {
      id: 'hiring_risk_delay',
      label: 'Hiring Risk & Delay',
      count: 3,
      delta: -1,
      severity: 'warning',
      topDriver: '2 seats >30d vacant; SRE pipeline at 0',
    },
    {
      id: 'guardrail_violations',
      label: 'Guardrail Violations',
      count: 6,
      delta: 2,
      severity: 'critical',
      topDriver: '1 hard constraint (span >12); 5 policy warnings',
    },
    {
      id: 'org_drift_alerts',
      label: 'Org Drift Alerts',
      count: 3,
      delta: 0,
      severity: 'warning',
      topDriver: 'Finance +2 HC vs approved; Eng spans drifted',
    },
  ],

  decisionDocket: [
    {
      id: 'dec-1',
      question: 'Reallocate Analytics capacity to Cloud Migration, or accept 6-week slip?',
      tradeoffs: ['2 FTE reallocation', 'Analytics -2w', 'Value at risk: unknown (costs not connected)'],
      linkedInitiativeKey: 'cloud-migration-p2',
      linkedBet: 'Cloud Migration Phase 2',
      urgency: 'critical',
      dueIn: 'Decide by Mon',
    },
    {
      id: 'dec-2',
      question: 'Approve contractor burst for SRE, or continue internal recruiting?',
      tradeoffs: ['Incremental spend: unknown (costs not connected)', 'Saves 5 weeks', 'Closes SRE gap'],
      linkedInitiativeKey: 'cloud-migration-p2',
      linkedBet: 'Cloud Migration Phase 2',
      urgency: 'critical',
      dueIn: 'Decide by Tue',
    },
    {
      id: 'dec-3',
      question: 'Phase APAC buildout (phased) or commit full scope now?',
      tradeoffs: ['Budget impact: unknown (costs not connected)', 'Q2 vs Q3 launch', '12 roles'],
      linkedInitiativeKey: 'apac-entry',
      linkedBet: 'APAC Market Entry',
      urgency: 'high',
      dueIn: 'Decide by Fri',
    },
    {
      id: 'dec-4',
      question: 'Backfill ML Lead internally or launch external search?',
      tradeoffs: ['Promote vs hire', '2w vs 12w lead', 'AI/ML continuity'],
      linkedInitiativeKey: 'aiml-infra',
      linkedBet: 'AI/ML Infrastructure',
      urgency: 'high',
      dueIn: 'Decide by Fri',
    },
  ],

  bets: [
    {
      key: 'cloud-migration-p2',
      name: 'Cloud Migration Phase 2',
      rag: 'red',
      weeksNeeded: 14,
      weeksLeft: 8,
      priorityDrivers: [
        { id: 'status', label: 'Status', value: 'RED', basis: 'derived', note: null, metric: null },
        { id: 'strategic_value', label: 'Strategic value', value: 'HIGH', basis: 'configuration', note: null, metric: null },
        { id: 'schedule_slip', label: 'Schedule slip', value: '+6w', basis: 'derived', note: null, metric: { value: 6, unit: 'weeks' } },
        { id: 'capacity_gap', label: 'Capacity gap', value: '~23 pts', basis: 'derived', note: null, metric: { value: 23, unit: 'points' } },
      ],
      accountability: 'Engineering → Platform → J. Chen',
      constraint: 'Capacity gap of ~23 points by deadline.',
      confidence: 'high',
      confidenceReason: 'Work signals snapshot.',
      proofs: [
        {
          basis: 'evidence',
          signal: 'SRE pipeline at zero',
          detail: '0 candidates in final stage after 45 days',
          confidence: 'high',
          confidenceReason: 'Not connected; manual note.',
          href: null,
        },
        {
          basis: 'evidence',
          signal: 'Sprint velocity down 34%',
          detail: 'Platform team stretched across 3 concurrent initiatives',
          confidence: 'high',
          confidenceReason: 'Work signals snapshot.',
          href: null,
        },
        {
          basis: 'evidence',
          signal: 'Dependency on Team Bravo',
          detail: 'API contract not finalized; blocking integration work',
          confidence: 'medium',
          confidenceReason: 'Manual update 3d ago',
          href: null,
        },
      ],
      options: [
        {
          kind: 'reallocate',
          action: 'Reallocate 2 FTEs from Analytics',
          delta: 'Closes gap in 4w; delays Analytics Q3 by 2w',
        },
        {
          kind: 'vendor',
          action: 'Approve contractor burst (2 SREs)',
          delta: 'Closes gap in 5w; contractor burst (cost unknown)',
        },
        {
          kind: 'do_nothing',
          action: 'Accept slip (~6w)',
          delta: 'No change to capacity, scope, or date (explicit decision).',
        },
      ],
    },
    {
      key: 'customer-360',
      name: 'Customer 360 Platform',
      rag: 'amber',
      weeksNeeded: 10,
      weeksLeft: 10,
      priorityDrivers: [
        { id: 'status', label: 'Status', value: 'AMBER', basis: 'derived', note: null, metric: null },
        { id: 'strategic_value', label: 'Strategic value', value: 'MED', basis: 'configuration', note: null, metric: null },
        { id: 'capacity_gap', label: 'Capacity gap', value: 'Unknown', basis: 'assumption', note: 'Mock does not include a capacity model.', metric: null },
      ],
      accountability: 'Product → Data Products → M. Torres',
      constraint: 'PM capacity at 120%; dual ownership conflict',
      confidence: 'medium',
      confidenceReason: 'OKRs not connected.',
      proofs: [
        {
          basis: 'evidence',
          signal: 'PM allocation conflict',
          detail: 'Single PM owns both Reporting and Integration workstreams',
          confidence: 'high',
          confidenceReason: 'Org data current',
          href: null,
        },
        {
          basis: 'evidence',
          signal: 'Data pipeline incomplete',
          detail: '3 of 8 integration sources completed',
          confidence: 'medium',
          confidenceReason: 'Manual update 5d ago',
          href: null,
        },
      ],
      options: [
        {
          kind: 'reallocate',
          action: 'Split PM ownership',
          delta: 'Adds focus; requires PM backfill (4w lead time)',
        },
        {
          kind: 'scope',
          action: 'Prioritize core 5 data sources',
          delta: 'MVP in 6w; defers edge case integrations',
        },
        {
          kind: 'do_nothing',
          action: 'No action',
          delta: 'No intervention selected (explicit decision).',
        },
      ],
    },
    {
      key: 'apac-entry',
      name: 'APAC Market Entry',
      rag: 'green',
      weeksNeeded: 6,
      weeksLeft: 12,
      priorityDrivers: [
        { id: 'status', label: 'Status', value: 'GREEN', basis: 'derived', note: null, metric: null },
        { id: 'strategic_value', label: 'Strategic value', value: 'MED', basis: 'configuration', note: null, metric: null },
      ],
      accountability: 'Sales → International → J. Wu',
      constraint: 'None — ahead of schedule',
      confidence: 'high',
      confidenceReason: 'Signals current.',
      proofs: [
        {
          basis: 'evidence',
          signal: 'Team fully staffed',
          detail: 'All 8 roles filled 2 weeks ahead of plan',
          confidence: 'high',
          confidenceReason: 'Org baseline snapshot.',
          href: null,
        },
      ],
      options: [],
    },
    {
      key: 'aiml-infra',
      name: 'AI/ML Infrastructure',
      rag: 'amber',
      weeksNeeded: 11,
      weeksLeft: 10,
      priorityDrivers: [
        { id: 'status', label: 'Status', value: 'AMBER', basis: 'derived', note: null, metric: null },
        { id: 'strategic_value', label: 'Strategic value', value: 'HIGH', basis: 'configuration', note: null, metric: null },
        { id: 'schedule_slip', label: 'Schedule slip', value: '+1w', basis: 'derived', note: null, metric: { value: 1, unit: 'weeks' } },
        { id: 'capacity_gap', label: 'Capacity gap', value: '~18 pts', basis: 'derived', note: null, metric: { value: 18, unit: 'points' } },
      ],
      accountability: 'Engineering → ML Platform → D. Park',
      constraint: 'GPU quota 80%; MLOps 1/3 filled',
      confidence: 'high',
      confidenceReason: 'Work signals snapshot.',
      proofs: [
        {
          basis: 'evidence',
          signal: 'GPU quota constraint',
          detail: 'Cloud GPU allocation at 80%, ceiling in 3 weeks',
          confidence: 'high',
          confidenceReason: 'Not connected; manual note.',
          href: null,
        },
        {
          basis: 'evidence',
          signal: 'MLOps hiring behind',
          detail: '1 of 3 MLOps roles filled; 2 in sourcing',
          confidence: 'high',
          confidenceReason: 'Not connected; manual note.',
          href: null,
        },
      ],
      options: [
        {
          kind: 'vendor',
          action: 'Increase GPU quota',
          delta: 'Removes infra constraint immediately (cost unknown)',
        },
        {
          kind: 'vendor',
          action: 'Contract MLOps support',
          delta: '2w onboard; bridges gap through Q2',
        },
        {
          kind: 'do_nothing',
          action: 'Accept slip (~1w)',
          delta: 'No change to capacity, scope, or date (explicit decision).',
        },
      ],
    },
    {
      key: 'soc2',
      name: 'SOC2 Compliance',
      rag: 'green',
      weeksNeeded: 4,
      weeksLeft: 8,
      priorityDrivers: [
        { id: 'status', label: 'Status', value: 'GREEN', basis: 'derived', note: null, metric: null },
        { id: 'strategic_value', label: 'Strategic value', value: 'LOW', basis: 'configuration', note: null, metric: null },
      ],
      accountability: 'Security → GRC → L. Martinez',
      constraint: 'None — final audit scheduled',
      confidence: 'high',
      confidenceReason: 'Audit trail complete',
      proofs: [
        {
          basis: 'evidence',
          signal: 'Controls verified',
          detail: '47 of 47 controls implemented and evidence collected',
          confidence: 'high',
          confidenceReason: 'Audit prep complete',
          href: null,
        },
      ],
      options: [],
    },
  ],

  capacityGaps: [
    {
      initiativeKey: 'cloud-migration-p2',
      name: 'Cloud Migration Phase 2',
      demandPoints: 100,
      supplyPointsByDeadline: 77,
      deltaPoints: -23,
      deltaPercent: -23,
    },
    {
      initiativeKey: 'aiml-infra',
      name: 'AI/ML Infrastructure',
      demandPoints: 60,
      supplyPointsByDeadline: 42,
      deltaPoints: -18,
      deltaPercent: -30,
    },
    {
      initiativeKey: 'customer-360',
      name: 'Customer 360 Platform',
      demandPoints: 50,
      supplyPointsByDeadline: 40,
      deltaPoints: -10,
      deltaPercent: -20,
    },
  ],

  evidenceCoverage: [
    {
      id: 'org_baseline',
      label: 'Org baseline',
      status: 'connected',
      sourceLabel: 'Org graph',
      detail: null,
    },
    {
      id: 'costs',
      label: 'Costs',
      status: 'not_connected',
      sourceLabel: null,
      detail: 'Not connected.',
    },
    {
      id: 'work_signals',
      label: 'Work signals',
      status: 'connected',
      sourceLabel: 'Execution telemetry snapshot',
      detail: 'As-of 2025-01-17T08:42:00-08:00.',
    },
    {
      id: 'hiring_pipeline',
      label: 'Hiring pipeline',
      status: 'not_connected',
      sourceLabel: null,
      detail: 'Not connected.',
    },
    {
      id: 'okrs',
      label: 'OKRs',
      status: 'not_connected',
      sourceLabel: null,
      detail: 'Not connected.',
    },
    {
      id: 'performance',
      label: 'Performance',
      status: 'not_connected',
      sourceLabel: null,
      detail: 'Not connected.',
    },
  ],
};
